import type { ImpactEffortPoint } from "@playbookai/widget-schemas";
import {
  DEFAULT_SCALE,
  tierFor,
  type ImpactEffortScale,
  type Tier,
} from "./model.js";

export interface FanOutOffset {
  readonly dx: number;
  readonly dy: number;
}

export interface FanOutLayout {
  readonly group: { readonly xPct: number; readonly yPct: number };
  readonly plot: { readonly width: number; readonly height: number };
  readonly gapPx?: number;
}

const TIER_FAN_ANGLE: Record<Tier, number> = {
  "quick-win": -Math.PI / 4,
  "major-projects": (-3 * Math.PI) / 4,
  "fill-ins": Math.PI / 4,
  avoid: (3 * Math.PI) / 4,
};

const angleDistance = (left: number, right: number) =>
  Math.abs(Math.atan2(Math.sin(left - right), Math.cos(left - right)));

const FAN_START_RADIUS_PX = 64;
const FAN_RADIUS_STEP_PX = 8;
const FAN_GAP_PX = 8;
const IMPACT_EFFORT_PILL_HEIGHT_PX = 24;

/**
 * Keep a compact radial fan, assigning colours to their nearest quadrant.
 * The radius grows until the rendered pill boxes no longer touch. When a plot
 * edge prevents a ring from opening cleanly, a deterministic nearest-slot
 * fallback packs the pills without overlap.
 */
export function fanOutPointOffsets(
  points: readonly ImpactEffortPoint[],
  scale: ImpactEffortScale = DEFAULT_SCALE,
  layout?: FanOutLayout,
): ReadonlyMap<string, FanOutOffset> {
  if (points.length === 0) return new Map();

  const pointTiers = points.map((point) =>
    tierFor(point.impact, point.effort, scale),
  );
  const sameVerticalHalf =
    pointTiers.every((tier) =>
      ["quick-win", "major-projects"].includes(tier),
    ) || pointTiers.every((tier) => ["fill-ins", "avoid"].includes(tier));
  const startAngle =
    new Set(pointTiers).size > 1 && sameVerticalHalf ? 0 : -Math.PI / 2;
  const gapPx = layout?.gapPx ?? FAN_GAP_PX;
  const maxRadius = layout
    ? Math.max(layout.plot.width, layout.plot.height)
    : 320;
  let latest = new Map<string, FanOutOffset>();

  for (
    let radius = FAN_START_RADIUS_PX;
    radius <= maxRadius;
    radius += FAN_RADIUS_STEP_PX
  ) {
    const radial = assignRadialSlots(points, scale, startAngle, radius);
    latest = layout ? constrainOffsetsToPlot(points, radial, layout) : radial;
    if (!fanOutOffsetsOverlap(points, latest, layout, gapPx)) return latest;
  }

  return layout
    ? packFanOutIntoOpenSlots(points, scale, layout, gapPx)
    : latest;
}

function assignRadialSlots(
  points: readonly ImpactEffortPoint[],
  scale: ImpactEffortScale,
  startAngle: number,
  radius: number,
): Map<string, FanOutOffset> {
  const offsets = new Map<string, FanOutOffset>();
  const availableSlots = points.map((_, index) => {
    const angle = (2 * Math.PI * index) / points.length + startAngle;
    return {
      angle,
      dx: Math.cos(angle) * radius,
      dy: Math.sin(angle) * radius,
    };
  });

  for (const tier of [
    "quick-win",
    "major-projects",
    "fill-ins",
    "avoid",
  ] as const) {
    for (const point of points.filter(
      (candidate) =>
        tierFor(candidate.impact, candidate.effort, scale) === tier,
    )) {
      let closestSlotIndex = 0;
      for (let index = 1; index < availableSlots.length; index += 1) {
        if (
          angleDistance(
            availableSlots[index]!.angle,
            scale.effortDirection === "ascending"
              ? Math.PI - TIER_FAN_ANGLE[tier]
              : TIER_FAN_ANGLE[tier],
          ) <
          angleDistance(
            availableSlots[closestSlotIndex]!.angle,
            scale.effortDirection === "ascending"
              ? Math.PI - TIER_FAN_ANGLE[tier]
              : TIER_FAN_ANGLE[tier],
          )
        ) {
          closestSlotIndex = index;
        }
      }
      const [slot] = availableSlots.splice(closestSlotIndex, 1);
      if (slot) offsets.set(point.id, { dx: slot.dx, dy: slot.dy });
    }
  }
  return offsets;
}

function constrainOffsetsToPlot(
  points: readonly ImpactEffortPoint[],
  offsets: ReadonlyMap<string, FanOutOffset>,
  layout: FanOutLayout,
): Map<string, FanOutOffset> {
  return new Map(
    points.map((point) => [
      point.id,
      constrainFanOutOffset(
        point,
        layout.group,
        layout.plot,
        offsets.get(point.id) ?? { dx: 0, dy: 0 },
      ),
    ]),
  );
}

function fanOutOffsetsOverlap(
  points: readonly ImpactEffortPoint[],
  offsets: ReadonlyMap<string, FanOutOffset>,
  layout: FanOutLayout | undefined,
  gapPx: number,
): boolean {
  const boxes = points.map((point) => {
    const pill = impactEffortPillSize(point.label);
    const offset = offsets.get(point.id) ?? { dx: 0, dy: 0 };
    const anchorX = layout
      ? impactEffortPillCenterXPx(
          layout.group.xPct,
          layout.plot.width,
          pill.width,
        )
      : 0;
    const anchorY = layout
      ? impactEffortPillCenterYPx(layout.group.yPct, layout.plot.height)
      : 0;
    return {
      x: anchorX + offset.dx,
      y: anchorY + offset.dy,
      ...pill,
    };
  });
  for (let left = 0; left < boxes.length; left += 1) {
    for (let right = left + 1; right < boxes.length; right += 1) {
      const a = boxes[left];
      const b = boxes[right];
      if (!a || !b) continue;
      const overlapsX = Math.abs(a.x - b.x) < (a.width + b.width) / 2 + gapPx;
      const overlapsY = Math.abs(a.y - b.y) < (a.height + b.height) / 2 + gapPx;
      if (overlapsX && overlapsY) return true;
    }
  }
  return false;
}

function packFanOutIntoOpenSlots(
  points: readonly ImpactEffortPoint[],
  scale: ImpactEffortScale,
  layout: FanOutLayout,
  gapPx: number,
): Map<string, FanOutOffset> {
  const offsets = new Map<string, FanOutOffset>();
  const occupied: Array<{
    x: number;
    y: number;
    width: number;
    height: number;
  }> = [];
  const groupY = impactEffortPillCenterYPx(
    layout.group.yPct,
    layout.plot.height,
  );
  const ordered = [...points].sort((left, right) => {
    const widthDelta =
      impactEffortPillSize(right.label).width -
      impactEffortPillSize(left.label).width;
    return widthDelta || left.id.localeCompare(right.id);
  });
  const fallbackOffsets = constrainOffsetsToPlot(
    ordered,
    assignRadialSlots(ordered, scale, -Math.PI / 2, FAN_START_RADIUS_PX),
    layout,
  );

  for (const point of ordered) {
    const pill = impactEffortPillSize(point.label);
    const groupX = impactEffortPillCenterXPx(
      layout.group.xPct,
      layout.plot.width,
      pill.width,
    );
    const tier = tierFor(point.impact, point.effort, scale);
    let selected:
      | {
          x: number;
          y: number;
          score: number;
        }
      | undefined;
    const minX = 4 + pill.width / 2;
    const maxX = Math.max(minX, layout.plot.width - 4 - pill.width / 2);
    const minY = 4 + pill.height / 2;
    const maxY = Math.max(minY, layout.plot.height - 4 - pill.height / 2);
    for (let y = minY; y <= maxY; y += pill.height + gapPx) {
      for (let x = minX; x <= maxX; x += 12) {
        const dx = x - groupX;
        const dy = y - groupY;
        if (Math.hypot(dx, dy) < FAN_START_RADIUS_PX) continue;
        const blocked = occupied.some(
          (box) =>
            Math.abs(x - box.x) < (pill.width + box.width) / 2 + gapPx &&
            Math.abs(y - box.y) < (pill.height + box.height) / 2 + gapPx,
        );
        if (blocked) continue;
        const angle = Math.atan2(dy, dx);
        const directionPenalty =
          angleDistance(
            angle,
            scale.effortDirection === "ascending"
              ? Math.PI - TIER_FAN_ANGLE[tier]
              : TIER_FAN_ANGLE[tier],
          ) * 1_000;
        const score = directionPenalty + Math.hypot(dx, dy);
        if (!selected || score < selected.score) selected = { x, y, score };
      }
    }
    if (!selected) {
      const fallback = fallbackOffsets.get(point.id) ?? { dx: 0, dy: 0 };
      selected = {
        x: groupX + fallback.dx,
        y: groupY + fallback.dy,
        score: Number.POSITIVE_INFINITY,
      };
    }
    occupied.push({ ...selected, ...pill });
    offsets.set(point.id, {
      dx: selected.x - groupX,
      dy: selected.y - groupY,
    });
  }
  return offsets;
}

export function impactEffortPillSize(label: string): {
  readonly width: number;
  readonly height: number;
} {
  const labelWidth = Math.min(140, label.length * 6.5);
  return {
    // 42px covers the grip, inter-item gap, action slot + margin, and border.
    width: Math.max(58, 42 + labelWidth),
    height: IMPACT_EFFORT_PILL_HEIGHT_PX,
  };
}

/** Return the rendered pill centre for the edge-aware CSS translation. */
export function impactEffortPillCenterXPx(
  xPct: number,
  plotWidth: number,
  pillWidth: number,
): number {
  const width = Math.max(1, plotWidth);
  const safeXPct = Math.max(0, Math.min(100, xPct));
  if (safeXPct === 0) return pillWidth / 2;
  if (safeXPct === 100) return width - pillWidth / 2;
  return (safeXPct / 100) * width;
}

/**
 * Map the complete score-coordinate range onto the visual range where a pill
 * remains inside the plot. A score coordinate of 0 touches the top edge and
 * 100 touches the bottom edge; intermediate values remain proportional.
 */
export function impactEffortScoreYPctToDisplayYPct(
  scoreYPct: number,
  plotHeight: number,
  pillHeight = IMPACT_EFFORT_PILL_HEIGHT_PX,
): number {
  const height = Math.max(1, plotHeight);
  const fittedPillHeight = Math.min(height, Math.max(0, pillHeight));
  const marginPct = (fittedPillHeight / 2 / height) * 100;
  const usablePct = 100 - marginPct * 2;
  const boundedScorePct = Math.max(0, Math.min(100, scoreYPct));
  return marginPct + (boundedScorePct / 100) * usablePct;
}

/** Reverse `impactEffortScoreYPctToDisplayYPct` for pointer interaction. */
export function impactEffortDisplayYPctToScoreYPct(
  displayYPct: number,
  plotHeight: number,
  pillHeight = IMPACT_EFFORT_PILL_HEIGHT_PX,
): number {
  const height = Math.max(1, plotHeight);
  const fittedPillHeight = Math.min(height, Math.max(0, pillHeight));
  const marginPct = (fittedPillHeight / 2 / height) * 100;
  const usablePct = 100 - marginPct * 2;
  if (usablePct <= 0) return 50;
  return Math.max(
    0,
    Math.min(100, ((displayYPct - marginPct) / usablePct) * 100),
  );
}

/** Return the visible pill centre for a score-coordinate Y percentage. */
export function impactEffortPillCenterYPx(
  yPct: number,
  plotHeight: number,
  pillHeight = IMPACT_EFFORT_PILL_HEIGHT_PX,
): number {
  const height = Math.max(1, plotHeight);
  return (
    (impactEffortScoreYPctToDisplayYPct(yPct, height, pillHeight) / 100) *
    height
  );
}

/** Preserve the fan direction while keeping the entire pill inside the plot. */
export function constrainFanOutOffset(
  point: ImpactEffortPoint,
  group: { readonly xPct: number; readonly yPct: number },
  plot: { readonly width: number; readonly height: number },
  offset: FanOutOffset,
  insetPx = 4,
): FanOutOffset {
  const width = Math.max(1, plot.width);
  const height = Math.max(1, plot.height);
  const pill = impactEffortPillSize(point.label);
  const xPct = Math.max(0, Math.min(100, group.xPct));
  const xCenter = impactEffortPillCenterXPx(xPct, width, pill.width);
  const candidateCenter = xCenter + offset.dx;
  const minCenter = insetPx + pill.width / 2;
  const maxCenter = Math.max(minCenter, width - insetPx - pill.width / 2);
  const visibleCenter = Math.max(
    minCenter,
    Math.min(maxCenter, candidateCenter),
  );

  const yAnchor = impactEffortPillCenterYPx(group.yPct, height, pill.height);
  const candidateTop = yAnchor + offset.dy - pill.height / 2;
  const maxTop = Math.max(insetPx, height - pill.height - insetPx);
  const visibleTop = Math.max(insetPx, Math.min(maxTop, candidateTop));

  return {
    dx: visibleCenter - xCenter,
    dy: visibleTop - yAnchor + pill.height / 2,
  };
}
